/**
 * AP-9 — Signal Trace / Plot (FM-WIN-10).
 *
 * Die Signalwerte werden hier im Main-Thread aus den Rohbytes des Frames
 * gerechnet, nicht im Worker: `cm_last_decode_json` liefert nur die
 * Kurzfassung `dbc.summary` (bis zu vier Signale als Text), der Plot braucht
 * aber jeden Zahlenwert einzeln. Die dafür nötigen Metadaten stehen im
 * `dbc`- bzw. `eds`-Snapshot (CONTRACTS.md §5.3), die Rechnung selbst ist
 * reine Bitarithmetik — dafür lohnt kein zweiter Weg durch das WASM-Modul.
 */

import { createButton, createCombo, createLabel, createSplitter } from '../ui/controls';
import { createFloatingPanel, type FloatingPanel } from './filters/FloatingPanel';
import { decodeSignalValue, type DbcMessageMeta, type DbcSnapshot } from './signalValue';
import type { EdsSnapshot } from './EdsBrowser';
import { createChartCanvas, createRedrawScheduler, drawChartFrame } from './chartCanvas';
import './viewsC.css';

const TRACE_LENGTHS: ReadonlyArray<{ label: string; seconds: number }> = [
  { label: '5m', seconds: 300 },
  { label: '2m', seconds: 120 },
  { label: '1m', seconds: 60 },
  { label: '30s', seconds: 30 },
  { label: '15s', seconds: 15 },
  { label: '10s', seconds: 10 },
  { label: '5s', seconds: 5 },
];

const DEFAULT_WINDOW_SECONDS = 30;
const REDRAW_INTERVAL_MS = 300;
const MAX_SAMPLES_PER_SIGNAL = 6000;

/** Luft zwischen der untersten Legendenzeile und der obersten Achsenbeschriftung. */
const LEGEND_BOTTOM_GAP = 14;
/** Mindestabstand zwischen zwei Legendenspalten. */
const LEGEND_COLUMN_GAP = 24;

/** `kPalette[]` — acht Farben, zyklisch in Listenreihenfolge vergeben. */
const PALETTE = [
  'rgb(96,165,250)',
  'rgb(255,160,0)',
  'rgb(0,217,126)',
  'rgb(224,48,48)',
  'rgb(192,128,255)',
  'rgb(0,200,224)',
  'rgb(255,213,0)',
  'rgb(255,112,176)',
] as const;

/** Anteilige Flächen je Pane-Anzahl (`kPaneLayouts`). */
const PANE_LAYOUTS: ReadonlyArray<ReadonlyArray<readonly [number, number, number, number]>> = [
  [[0, 0, 1, 1]],
  [
    [0, 0, 1, 0.5],
    [0, 0.5, 1, 1],
  ],
  [
    [0, 0, 1, 1 / 3],
    [0, 1 / 3, 1, 2 / 3],
    [0, 2 / 3, 1, 1],
  ],
  [
    [0, 0, 0.5, 0.5],
    [0.5, 0, 1, 0.5],
    [0, 0.5, 0.5, 1],
    [0.5, 0.5, 1, 1],
  ],
];

interface Sample {
  t: number;
  v: number;
}

interface PlotSignal {
  key: string;
  unit: string;
  color: string;
  selected: boolean;
  hasValue: boolean;
  lastValue: number;
  samples: Sample[];
  /** CANopen-PDO-Eintrag statt DBC-Signal. */
  isPdo: boolean;
  /** Nur für PDO-Einträge: die COB-ID, an der sie hängen. */
  frameId: number;
  bitOffset: number;
  bitLength: number;
  message?: DbcMessageMeta;
  signalIndex: number;
}

/**
 * `GroupSelectedSignalsByRange()`: nach eigenem Maximum aufsteigend sortieren,
 * dann in zusammenhängende, möglichst gleich große Blöcke teilen. Dadurch
 * landen Signale mit ähnlichem Wertebereich auf derselben Fläche.
 */
export function groupSignalsByRange<T extends { samples: Sample[]; hasValue: boolean; lastValue: number }>(
  selected: readonly T[],
  panes: number,
): T[][] {
  const maxOf = (signal: T): number => {
    if (signal.samples.length === 0) return signal.hasValue ? signal.lastValue : 0;
    let hi = signal.samples[0]!.v;
    for (const sample of signal.samples) hi = Math.max(hi, sample.v);
    return hi;
  };
  const sorted = [...selected].sort((a, b) => maxOf(a) - maxOf(b));
  const count = Math.max(1, panes);
  const groups: T[][] = Array.from({ length: count }, () => []);
  const base = Math.floor(sorted.length / count);
  const remainder = sorted.length % count;
  let index = 0;
  for (let pane = 0; pane < count && index < sorted.length; pane += 1) {
    const take = base + (pane < remainder ? 1 : 0);
    for (let i = 0; i < take && index < sorted.length; i += 1, index += 1) {
      groups[pane]!.push(sorted[index]!);
    }
  }
  return groups;
}

/** `J1939PgnKey()`: Priorität und Quelladresse ausblenden, bei PDU1 auch das Ziel. */
export function j1939PgnKey(id29: number): number {
  const pf = (id29 >>> 16) & 0xff;
  const dp = (id29 >>> 24) & 0x03;
  const pgn = pf < 240 ? (dp << 16) | (pf << 8) : (dp << 16) | (pf << 8) | ((id29 >>> 8) & 0xff);
  return pgn >>> 0;
}

/** `ExtractPdoBits()`: LSB-first, ohne Vorzeichen und ohne Skalierung, maximal 64 Bit. */
export function extractPdoBits(data: Uint8Array, dataLen: number, bitOffset: number, bitLength: number): number {
  if (bitLength <= 0 || bitLength > 64) return 0;
  let raw = 0n;
  for (let i = 0; i < bitLength; i += 1) {
    const bitPos = bitOffset + i;
    const byteIndex = bitPos >> 3;
    if (byteIndex >= dataLen) break;
    if (((data[byteIndex] ?? 0) >> (bitPos & 7)) & 1) raw |= 1n << BigInt(i);
  }
  return Number(raw);
}

/** `"%.3g"` der Achsenbeschriftung: drei signifikante Stellen. */
function format3g(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  if (value === 0) return '0';
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  if (exponent < -4 || exponent >= 3) {
    return value.toExponential(2).replace('e+', 'e+').replace(/\.?0+e/, 'e');
  }
  return String(Number(value.toPrecision(3)));
}

interface PdoMappingEntry {
  index: number;
  subIndex: number;
  bitLength: number;
}

function parseEdsNumber(text: string): number {
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  const value = trimmed.toLowerCase().startsWith('0x') ? Number.parseInt(trimmed, 16) : Number(trimmed);
  return Number.isFinite(value) ? value : 0;
}

/** Vordefinierter Verteilersatz CiA 301: COB-ID -> PDO-Nummer, Richtung, Node-ID. */
export function pdoFromCobId(id: number): { pdoNumber: number; isTx: boolean; nodeId: number } | null {
  if (id > 0x7ff) return null;
  const base = id & 0x780;
  const nodeId = id & 0x7f;
  if (nodeId === 0 || nodeId > 127) return null;
  switch (base) {
    case 0x180:
      return { pdoNumber: 1, isTx: true, nodeId };
    case 0x200:
      return { pdoNumber: 1, isTx: false, nodeId };
    case 0x280:
      return { pdoNumber: 2, isTx: true, nodeId };
    case 0x300:
      return { pdoNumber: 2, isTx: false, nodeId };
    case 0x380:
      return { pdoNumber: 3, isTx: true, nodeId };
    case 0x400:
      return { pdoNumber: 3, isTx: false, nodeId };
    case 0x480:
      return { pdoNumber: 4, isTx: true, nodeId };
    case 0x500:
      return { pdoNumber: 4, isTx: false, nodeId };
    default:
      return null;
  }
}

export function readPdoMapping(eds: EdsSnapshot, mappingIndex: number): PdoMappingEntry[] {
  const object = eds.entries.find((entry) => entry.index === mappingIndex);
  if (object === undefined) return [];
  const countSub = object.subs.find((sub) => sub.subIndex === 0);
  const count = countSub === undefined ? 0 : parseEdsNumber(countSub.defaultValue);
  const result: PdoMappingEntry[] = [];
  for (let sub = 1; sub <= count && sub <= 64; sub += 1) {
    const entry = object.subs.find((candidate) => candidate.subIndex === sub);
    if (entry === undefined) continue;
    const packed = parseEdsNumber(entry.defaultValue);
    if (packed === 0) continue;
    result.push({
      index: (packed >>> 16) & 0xffff,
      subIndex: (packed >>> 8) & 0xff,
      bitLength: packed & 0xff,
    });
  }
  return result;
}

function pdoEntryLabel(eds: EdsSnapshot, entry: PdoMappingEntry): string {
  const object = eds.entries.find((candidate) => candidate.index === entry.index);
  if (object !== undefined) {
    if (entry.subIndex === 0) return object.parameterName;
    const sub = object.subs.find((candidate) => candidate.subIndex === entry.subIndex);
    if (sub !== undefined && sub.parameterName !== '') return sub.parameterName;
    if (object.parameterName !== '') return object.parameterName;
  }
  const index = entry.index.toString(16).toUpperCase().padStart(4, '0');
  const sub = entry.subIndex.toString(16).toUpperCase().padStart(2, '0');
  return `${index}:${sub}`;
}

export interface SignalPlotOptions {
  /** Liefert `getSnapshot('dbc')`. */
  loadDbcSnapshot: () => Promise<DbcSnapshot>;
  /** Liefert `getSnapshot('eds')` — für die PDO-Zuordnung. */
  loadEdsSnapshot: () => Promise<EdsSnapshot>;
}

export interface SignalPlotHandle {
  panel: FloatingPanel;
  show(): void;
  /** Nach dem Laden einer DBC/EDS aufrufen. */
  refresh(): Promise<void>;
  /** Jeden empfangenen Frame einspeisen (`OnFrame()`). */
  onFrame(id: number, data: Uint8Array, dlc: number, timeMs: number): void;
  dispose(): void;
}

export function createSignalPlot(options: SignalPlotOptions): SignalPlotHandle {
  const panel = createFloatingPanel('Signal Trace / Plot');
  panel.element.style.width = 'calc(860px * var(--ui-scale))';

  const view = document.createElement('div');
  view.className = 'cm-plot-view';

  let windowSeconds = DEFAULT_WINDOW_SECONDS;
  let paneCount = 1;
  let signalsVisible = true;

  const toolbar = document.createElement('div');
  toolbar.className = 'cm-plot-view__toolbar';
  const closeButton = createButton('Close Window', { onClick: () => panel.hide() });
  const toggleButton = createButton('Hide Signals', {
    onClick: () => {
      signalsVisible = !signalsVisible;
      signalsPane.hidden = !signalsVisible;
      splitter.style.display = signalsVisible ? '' : 'none';
      toggleButton.textContent = signalsVisible ? 'Hide Signals' : 'Add Signals';
      redraw.invalidate();
    },
  });
  const clearButton = createButton('Clear History', {
    onClick: () => {
      for (const signal of signals) {
        signal.samples.length = 0;
        signal.hasValue = false;
      }
      redraw.invalidate();
    },
  });
  toolbar.append(
    closeButton,
    toggleButton,
    createLabel('Trace Length:'),
    createCombo({
      items: TRACE_LENGTHS.map((entry) => ({ value: String(entry.seconds), label: entry.label })),
      value: String(DEFAULT_WINDOW_SECONDS),
      onChange: (value) => {
        windowSeconds = Number(value);
        redraw.invalidate();
      },
    }),
    createLabel('Trace Count:'),
    createCombo({
      items: [1, 2, 3, 4].map((count) => ({ value: String(count), label: String(count) })),
      value: '1',
      onChange: (value) => {
        paneCount = Number(value);
        redraw.invalidate();
      },
    }),
    clearButton,
  );

  const body = document.createElement('div');
  body.className = 'cm-plot-view__body';

  const signalsPane = document.createElement('div');
  signalsPane.className = 'cm-plot-view__signals';
  const signalsLabel = createLabel('Signals (click to plot):');
  const list = document.createElement('div');
  list.className = 'cm-signal-list';
  const listHead = document.createElement('div');
  listHead.className = 'cm-signal-list__head';
  const headCheck = document.createElement('span');
  const headSignal = document.createElement('span');
  headSignal.textContent = 'Signal';
  const headValue = document.createElement('span');
  headValue.textContent = 'Value';
  headValue.className = 'cm-signal-list__value';
  listHead.append(headCheck, headSignal, headValue);
  const listBody = document.createElement('div');
  listBody.className = 'cm-signal-list__body';
  list.append(listHead, listBody);
  signalsPane.append(signalsLabel, list);

  const chart = createChartCanvas('cm-plot-canvas');

  const splitter = createSplitter(body, {
    orientation: 'vertical',
    minRatio: 0.15,
    onRatioChanged: (ratio) => {
      body.style.setProperty('--split-ratio', String(ratio));
      redraw.invalidate();
    },
  });
  body.append(signalsPane, splitter, chart.element);
  view.append(toolbar, body);
  panel.body.append(view);

  let signals: PlotSignal[] = [];
  let dbc: DbcSnapshot = { messages: [] };
  let eds: EdsSnapshot | null = null;
  const seenFrameIds = new Set<number>();
  const seenPgnKeys = new Set<number>();
  /** Signale je exakter Frame-ID bzw. je PGN-Schlüssel — spart die Suche im Empfangspfad. */
  const byFrameId = new Map<number, PlotSignal[]>();
  const byPgnKey = new Map<number, PlotSignal[]>();
  let listDirty = false;
  const valueCells = new Map<PlotSignal, HTMLElement>();

  const redraw = createRedrawScheduler(drawPlot);
  const resizeObserver = new ResizeObserver(() => redraw.invalidate());
  resizeObserver.observe(chart.element);

  // 300-ms-Takt wie `kRedrawTimerId`; baut bei Bedarf zuerst die Signalliste neu.
  const timer = window.setInterval(() => {
    if (!panel.visible) return;
    if (listDirty) rebuildSignalList();
    updateValueCells();
    redraw.invalidate();
  }, REDRAW_INTERVAL_MS);

  function rebuildSignalList(): void {
    listDirty = false;
    const previous = new Map(signals.map((signal) => [signal.key, signal]));
    const next: PlotSignal[] = [];

    for (const message of dbc.messages) {
      // Werkzeug-Artefakt ohne echte Botschaft.
      if (message.name === 'VECTOR__INDEPENDENT_SIG_MSG') continue;
      let seen = seenFrameIds.has(message.frameId);
      if (!seen && message.extended) seen = seenPgnKeys.has(j1939PgnKey(message.frameId));
      if (!seen) continue;
      message.signals.forEach((signal, index) => {
        const key = `${message.name}.${signal.name}`;
        const existing = previous.get(key);
        next.push(
          existing ?? {
            key,
            unit: signal.unit,
            color: '',
            selected: false,
            hasValue: false,
            lastValue: 0,
            samples: [],
            isPdo: false,
            frameId: message.frameId,
            bitOffset: 0,
            bitLength: 0,
            message,
            signalIndex: index,
          },
        );
      });
    }

    if (eds !== null) {
      for (const frameId of seenFrameIds) {
        const pdo = pdoFromCobId(frameId);
        if (pdo === null) continue;
        const mappingIndex = (pdo.isTx ? 0x1a00 : 0x1600) + (pdo.pdoNumber - 1);
        const mapping = readPdoMapping(eds, mappingIndex);
        let bitOffset = 0;
        for (const entry of mapping) {
          const label = pdoEntryLabel(eds, entry);
          const key = `PDO${pdo.pdoNumber} (${pdo.isTx ? 'Tx' : 'Rx'}) Node${pdo.nodeId}.${label}`;
          const existing = previous.get(key);
          next.push(
            existing ?? {
              key,
              unit: '',
              color: '',
              selected: false,
              hasValue: false,
              lastValue: 0,
              samples: [],
              isPdo: true,
              frameId,
              bitOffset,
              bitLength: entry.bitLength,
              signalIndex: 0,
            },
          );
          bitOffset += entry.bitLength;
        }
      }
    }

    signals = next;
    signals.forEach((signal, index) => {
      signal.color = PALETTE[index % PALETTE.length]!;
    });

    byFrameId.clear();
    byPgnKey.clear();
    for (const signal of signals) {
      const bucket = byFrameId.get(signal.frameId) ?? [];
      bucket.push(signal);
      byFrameId.set(signal.frameId, bucket);
      if (!signal.isPdo && signal.message?.extended === true) {
        const key = j1939PgnKey(signal.frameId);
        const pgnBucket = byPgnKey.get(key) ?? [];
        pgnBucket.push(signal);
        byPgnKey.set(key, pgnBucket);
      }
    }

    renderList();
  }

  function renderList(): void {
    listBody.replaceChildren();
    valueCells.clear();
    if (signals.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'cm-signal-list__empty';
      empty.textContent = 'Noch keine bekannten Signale auf dem Bus.';
      listBody.append(empty);
      return;
    }
    for (const signal of signals) {
      const row = document.createElement('div');
      row.className = 'cm-signal-list__row';
      row.dataset['selected'] = signal.selected ? '1' : '0';

      const check = document.createElement('input');
      check.type = 'checkbox';
      check.className = 'cm-signal-list__check';
      check.checked = signal.selected;
      check.tabIndex = -1;

      const name = document.createElement('span');
      name.className = 'cm-signal-list__name';
      name.textContent = signal.key;
      name.style.color = signal.selected ? signal.color : '';

      const value = document.createElement('span');
      value.className = 'cm-signal-list__value';
      value.textContent = signal.hasValue ? signal.lastValue.toFixed(3) : '';
      valueCells.set(signal, value);

      // Ein Klick auf die ganze Zeile schaltet um (OnRowClicked), nicht erst der zweite.
      row.addEventListener('click', () => {
        signal.selected = !signal.selected;
        check.checked = signal.selected;
        row.dataset['selected'] = signal.selected ? '1' : '0';
        name.style.color = signal.selected ? signal.color : '';
        redraw.invalidate();
      });

      row.append(check, name, value);
      listBody.append(row);
    }
  }

  function updateValueCells(): void {
    for (const [signal, cell] of valueCells) {
      cell.textContent = signal.hasValue ? signal.lastValue.toFixed(3) : '';
    }
  }

  function pushSample(signal: PlotSignal, value: number, timeMs: number): void {
    signal.lastValue = value;
    signal.hasValue = true;
    if (!signal.selected) return;
    signal.samples.push({ t: timeMs, v: value });
    if (signal.samples.length > MAX_SAMPLES_PER_SIGNAL) signal.samples.shift();
  }

  function drawPlot(): void {
    chart.element.style.height = `${Math.max(200, body.clientHeight)}px`;
    chart.sync();
    drawChartFrame(chart);
    const { ctx, width, height, palette } = chart;
    const style = getComputedStyle(chart.element);
    const fontSize = Number.parseFloat(style.fontSize) || 12;
    ctx.font = `${fontSize}px ${style.fontFamily}`;
    ctx.textBaseline = 'middle';

    const now = performance.now();
    for (const signal of signals) {
      while (signal.samples.length > 0 && (now - signal.samples[0]!.t) / 1000 > windowSeconds) {
        signal.samples.shift();
      }
    }

    const selected = signals.filter((signal) => signal.selected);
    if (selected.length === 0) {
      ctx.fillStyle = palette.textDim;
      ctx.textAlign = 'center';
      ctx.fillText('Check one or more signals on the left to plot them here.', width / 2, height / 2);
      return;
    }

    const groups = groupSignalsByRange(selected, paneCount);
    const layout = PANE_LAYOUTS[Math.min(Math.max(paneCount, 1), 4) - 1]!;
    groups.forEach((group, index) => {
      const cell = layout[index];
      if (cell === undefined) return;
      drawPane(group, {
        left: cell[0] * width,
        top: cell[1] * height,
        right: cell[2] * width,
        bottom: cell[3] * height,
      });
    });
  }

  function drawPane(
    group: PlotSignal[],
    rect: { left: number; top: number; right: number; bottom: number },
  ): void {
    const { ctx, palette } = chart;
    const style = getComputedStyle(chart.element);
    const fontSize = Number.parseFloat(style.fontSize) || 12;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.left, rect.top, rect.right - rect.left, rect.bottom - rect.top);
    ctx.clip();

    ctx.strokeStyle = palette.border;
    ctx.lineWidth = 1;
    ctx.strokeRect(rect.left + 0.5, rect.top + 0.5, rect.right - rect.left - 1, rect.bottom - rect.top - 1);

    const legendLeft = rect.left + 8;
    const legendAvailable = Math.max(60, rect.right - 10 - legendLeft);
    const legendTexts = group.map(
      (signal) =>
        `${signal.key}${signal.hasValue ? ': ' : ' (no data)'}${signal.lastValue.toFixed(3)} ${signal.unit}`,
    );
    let legendEntryWidth = 0;
    for (const text of legendTexts) {
      legendEntryWidth = Math.max(legendEntryWidth, 22 + ctx.measureText(text).width);
    }
    const legendColumns = Math.max(
      1,
      Math.min(group.length, Math.floor(legendAvailable / (legendEntryWidth + LEGEND_COLUMN_GAP))),
    );
    const legendColumnWidth = legendAvailable / legendColumns;
    const legendRows = Math.max(1, Math.ceil(group.length / legendColumns));
    const legendHeight = legendRows * (fontSize + 6) + 4 + LEGEND_BOTTOM_GAP;
    const labelWidth = ctx.measureText('-0.000e+0').width;
    const dataLeft = rect.left + 8 + labelWidth + 6;
    const dataRight = rect.right - 10;
    const dataTop = rect.top + legendHeight;
    const dataBottom = rect.bottom - fontSize - 10;
    if (dataRight <= dataLeft || dataBottom <= dataTop) {
      ctx.restore();
      return;
    }

    // Y-Achse dynamisch aus allen Signalen dieser Fläche.
    let axisMin = 0;
    let axisMax = 1;
    let hasRef = false;
    for (const signal of group) {
      if (signal.samples.length < 2) continue;
      let lo = signal.samples[0]!.v;
      let hi = lo;
      for (const sample of signal.samples) {
        lo = Math.min(lo, sample.v);
        hi = Math.max(hi, sample.v);
      }
      if (!hasRef) {
        axisMin = lo;
        axisMax = hi;
        hasRef = true;
      } else {
        axisMin = Math.min(axisMin, lo);
        axisMax = Math.max(axisMax, hi);
      }
    }
    if (hasRef && axisMax - axisMin < 1e-9) {
      axisMax += 1;
      axisMin -= 1;
    }

    const gridRows = 5;
    for (let row = 0; row <= gridRows; row += 1) {
      const y = dataTop + ((dataBottom - dataTop) * row) / gridRows;
      if (row > 0 && row < gridRows) {
        ctx.strokeStyle = palette.grid;
        ctx.beginPath();
        ctx.moveTo(dataLeft, Math.round(y) + 0.5);
        ctx.lineTo(dataRight, Math.round(y) + 0.5);
        ctx.stroke();
      }
      if (!hasRef) continue;
      ctx.fillStyle = palette.textDim;
      ctx.textAlign = 'right';
      ctx.fillText(format3g(axisMax - ((axisMax - axisMin) * row) / gridRows), dataLeft - 6, y);
    }

    // X-Achse: Alter aus der Position, "now" am rechten Rand.
    const axisY = dataBottom + fontSize * 0.7 + 2;
    ctx.fillStyle = palette.textDim;
    const columns = 4;
    for (let col = 0; col <= columns; col += 1) {
      const x = dataLeft + ((dataRight - dataLeft) * col) / columns;
      const age = (windowSeconds * (dataRight - x)) / (dataRight - dataLeft);
      ctx.textAlign = col === 0 ? 'left' : col === columns ? 'right' : 'center';
      ctx.fillText(age < 0.5 ? 'now' : `-${age.toFixed(0)}s`, x, axisY);
    }

    // Legende: Farbfeld + "Key: Wert Einheit" (Format aus DrawPlot()), spaltenweise
    // ueber die volle Breite verteilt statt als eine schmale Spalte.
    const legendTop = rect.top + 6 + fontSize / 2;
    ctx.textAlign = 'left';
    group.forEach((signal, index) => {
      const column = Math.floor(index / legendRows);
      const row = index % legendRows;
      const x = legendLeft + column * legendColumnWidth;
      const y = legendTop + row * (fontSize + 6);
      ctx.fillStyle = signal.color;
      ctx.fillRect(x, y - 5, 10, 10);
      ctx.fillStyle = palette.textMuted;
      ctx.fillText(legendTexts[index]!, x + 14, y);
    });

    if (!hasRef) {
      ctx.restore();
      return;
    }

    const span = axisMax - axisMin;
    ctx.lineWidth = 1.6;
    ctx.lineJoin = 'round';
    for (const signal of group) {
      if (signal.samples.length < 2) continue;
      ctx.strokeStyle = signal.color;
      ctx.beginPath();
      let started = false;
      for (const sample of signal.samples) {
        const age = (performance.now() - sample.t) / 1000;
        if (age > windowSeconds) continue;
        const x = dataRight - (age / windowSeconds) * (dataRight - dataLeft);
        const y = dataBottom - ((sample.v - axisMin) / span) * (dataBottom - dataTop);
        if (started) ctx.lineTo(x, y);
        else {
          ctx.moveTo(x, y);
          started = true;
        }
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  async function refresh(): Promise<void> {
    dbc = await options.loadDbcSnapshot();
    try {
      eds = await options.loadEdsSnapshot();
    } catch {
      eds = null;
    }
    listDirty = true;
    if (panel.visible) rebuildSignalList();
  }

  return {
    panel,
    show(): void {
      panel.show();
      void refresh();
      redraw.invalidate();
    },
    refresh,
    onFrame(id, data, dlc, timeMs): void {
      // Wie im Original: unsichtbar wird nichts gesammelt, auch keine IDs.
      if (!panel.visible) return;
      if (!seenFrameIds.has(id)) {
        seenFrameIds.add(id);
        if (id > 0x7ff) seenPgnKeys.add(j1939PgnKey(id));
        listDirty = true;
      }

      const exact = byFrameId.get(id);
      const matches = exact ?? byPgnKey.get(j1939PgnKey(id));
      if (matches === undefined) return;
      for (const signal of matches) {
        if (signal.isPdo) {
          if (signal.frameId !== id) continue;
          pushSample(signal, extractPdoBits(data, dlc, signal.bitOffset, signal.bitLength), timeMs);
          continue;
        }
        const meta = signal.message?.signals[signal.signalIndex];
        if (meta === undefined) continue;
        pushSample(signal, decodeSignalValue(meta, data, dlc), timeMs);
      }
    },
    dispose(): void {
      window.clearInterval(timer);
      resizeObserver.disconnect();
      redraw.cancel();
      chart.dispose();
    },
  };
}
